import {
  documentCapabilitiesResponseSchema,
  documentErrorResponseSchema,
  documentFilesResponseSchema,
  documentJobResponseSchema,
  documentJobsResponseSchema,
  documentUsageResponseSchema,
  downloadUrlResponseSchema,
  finalizeUploadResponseSchema,
  uploadSessionResponseSchema,
  type CreateJobRequest,
  type DocumentAsset,
  type DocumentCapability,
  type DocumentJob,
  type DocumentUsage,
} from "@xhalo/document-contracts";
import { sha256Hex } from "@xhalo/document-browser-engine";

type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
const MAX_DOCUMENT_BYTES = 100 * 1024 * 1024;

const mimeByExtension = new Map<string, string>([
  ["pdf", "application/pdf"], ["doc", "application/msword"], ["docx", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  ["xls", "application/vnd.ms-excel"], ["xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  ["ppt", "application/vnd.ms-powerpoint"], ["pptx", "application/vnd.openxmlformats-officedocument.presentationml.presentation"],
  ["odt", "application/vnd.oasis.opendocument.text"], ["ods", "application/vnd.oasis.opendocument.spreadsheet"], ["odp", "application/vnd.oasis.opendocument.presentation"],
  ["rtf", "application/rtf"], ["txt", "text/plain"], ["html", "text/html"], ["htm", "text/html"],
  ["epub", "application/epub+zip"], ["azw3", "application/vnd.amazon.ebook"], ["mobi", "application/x-mobipocket-ebook"],
  ["fb2", "application/x-fictionbook+xml"], ["cbz", "application/vnd.comicbook+zip"],
  ["png", "image/png"], ["jpg", "image/jpeg"], ["jpeg", "image/jpeg"],
]);
const zipExtensions = new Set(["docx", "xlsx", "pptx", "odt", "ods", "odp", "epub", "cbz"]);
const compoundExtensions = new Set(["doc", "xls", "ppt"]);

function fileExtension(name: string): string {
  const separator = name.lastIndexOf(".");
  return separator > -1 ? name.slice(separator + 1).toLowerCase() : "";
}

export function documentMimeForFile(file: Pick<File, "name" | "type">): string | undefined {
  const mime = mimeByExtension.get(fileExtension(file.name));
  return mime && (!file.type || file.type === mime) ? mime : undefined;
}

function hasDocumentSignature(bytes: Uint8Array, extension: string): boolean {
  if (extension === "pdf") return bytes.length >= 5 && new TextDecoder("ascii").decode(bytes.subarray(0, 5)) === "%PDF-";
  if (zipExtensions.has(extension)) return bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b
    && ((bytes[2] === 0x03 && bytes[3] === 0x04) || (bytes[2] === 0x05 && bytes[3] === 0x06) || (bytes[2] === 0x07 && bytes[3] === 0x08));
  if (compoundExtensions.has(extension)) return bytes.length >= 8
    && [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1].every((value, index) => bytes[index] === value);
  if (extension === "rtf") return new TextDecoder("ascii").decode(bytes.subarray(0, 5)) === "{\\rtf";
  const prefix = new TextDecoder().decode(bytes.subarray(0, 4096));
  if (extension === "txt") return !bytes.subarray(0, 4096).includes(0) && prefix.length > 0;
  if (extension === "html" || extension === "htm") return /<(?:!doctype\s+html|html)(?:\s|>)/i.test(prefix.trimStart());
  if (extension === "fb2") return /<FictionBook(?:\s|>)/i.test(prefix);
  if (extension === "mobi" || extension === "azw3") return bytes.length >= 68 && new TextDecoder("ascii").decode(bytes.subarray(60, 68)) === "BOOKMOBI";
  if (extension === "png") return bytes.length >= 8 && [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((value, index) => bytes[index] === value);
  if (extension === "jpg" || extension === "jpeg") return bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
  return false;
}

export class DocumentApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
    readonly retryable: boolean,
    readonly requestId?: string,
  ) {
    super(message);
  }
}

function assetIdOf(asset: { assetId?: string | undefined; id?: string | undefined }): string {
  const assetId = asset.assetId ?? asset.id;
  if (!assetId) throw new DocumentApiError("The file service did not return an asset ID.", "ASSET_RESPONSE_INVALID", 502, true);
  return assetId;
}

export function defaultDocumentApiBaseUrl(): string {
  const configured = import.meta.env.VITE_DOCUMENT_API_URL?.trim();
  if (configured) return configured.replace(/\/$/, "");
  if (typeof window !== "undefined") {
    if (window.location.hostname === "pdf-preview.xhalo.co") return "https://api-pdf-preview.xhalo.co";
    if (window.location.hostname === "pdf.xhalo.co") return "https://api-pdf.xhalo.co";
  }
  return "http://127.0.0.1:8787";
}

export class DocumentApiClient {
  private readonly baseUrl: string;

  constructor(baseUrl = defaultDocumentApiBaseUrl(), private readonly fetcher: Fetcher = (...args) => globalThis.fetch(...args)) {
    this.baseUrl = baseUrl.replace(/\/$/, "");
  }

  private async apiRequest(path: string, init: RequestInit = {}): Promise<unknown> {
    const headers = new Headers(init.headers);
    if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
    const response = await this.fetcher(`${this.baseUrl}${path}`, {
      ...init,
      headers,
      credentials: "include",
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const parsed = documentErrorResponseSchema.safeParse(body);
      if (parsed.success) {
        const error = parsed.data.error;
        throw new DocumentApiError(error.message, error.code, response.status, error.retryable, error.requestId);
      }
      throw new DocumentApiError("The document service returned an invalid response.", "DOCUMENT_API_INVALID_RESPONSE", response.status, response.status >= 500);
    }
    return body;
  }

  async getCapabilities(): Promise<readonly DocumentCapability[]> {
    return documentCapabilitiesResponseSchema.parse(await this.apiRequest("/v1/document/capabilities")).capabilities;
  }

  async uploadPdf(file: File): Promise<string> {
    if (file.size > MAX_DOCUMENT_BYTES) {
      throw new DocumentApiError("The PDF exceeds the upload limit.", "PDF_SIZE_LIMIT_EXCEEDED", 413, false);
    }
    if ((file.type && file.type !== "application/pdf") || !file.name.toLowerCase().endsWith(".pdf")) {
      throw new DocumentApiError("The selected file is not a PDF.", "MIME_TYPE_MISMATCH", 415, false);
    }
    const bytes = new Uint8Array(await file.arrayBuffer());
    if (bytes.byteLength < 5 || new TextDecoder("ascii").decode(bytes.subarray(0, 5)) !== "%PDF-") {
      throw new DocumentApiError("The selected file does not have a PDF signature.", "MIME_TYPE_MISMATCH", 415, false);
    }
    const checksumSha256 = await sha256Hex(bytes);
    const session = uploadSessionResponseSchema.parse(await this.apiRequest("/v1/document/upload-sessions", {
      method: "POST",
      body: JSON.stringify({ mime: "application/pdf", bytes: file.size, checksumSha256, filename: file.name }),
    }));
    if (session.asset) return assetIdOf(session.asset);
    if (!session.uploadSessionId || !session.uploadUrl) {
      throw new DocumentApiError("The file service did not create an upload session.", "UPLOAD_SESSION_INVALID", 502, true);
    }
    const upload = await this.fetcher(session.uploadUrl, {
      method: "PUT",
      headers: session.requiredHeaders,
      body: file,
      credentials: "omit",
    });
    if (!upload.ok) throw new DocumentApiError("The file upload failed.", "UPLOAD_FAILED", upload.status, upload.status >= 500);
    const finalized = finalizeUploadResponseSchema.parse(await this.apiRequest(`/v1/document/upload-sessions/${encodeURIComponent(session.uploadSessionId)}/finalize`, {
      method: "POST",
      body: JSON.stringify({ filename: file.name }),
    }));
    return assetIdOf(finalized.asset);
  }

  async uploadDocument(file: File): Promise<string> {
    if (file.size > MAX_DOCUMENT_BYTES) {
      throw new DocumentApiError("The document exceeds the upload limit.", "DOCUMENT_SIZE_LIMIT_EXCEEDED", 413, false);
    }
    const extension = fileExtension(file.name);
    const mime = documentMimeForFile(file);
    if (!mime) {
      throw new DocumentApiError("The selected file type does not match its extension.", "MIME_TYPE_MISMATCH", 415, false);
    }
    const bytes = new Uint8Array(await file.arrayBuffer());
    if (!hasDocumentSignature(bytes, extension)) {
      throw new DocumentApiError("The selected file signature is invalid.", "MIME_TYPE_MISMATCH", 415, false);
    }
    const checksumSha256 = await sha256Hex(bytes);
    const session = uploadSessionResponseSchema.parse(await this.apiRequest("/v1/document/upload-sessions", {
      method: "POST",
      body: JSON.stringify({ mime, bytes: file.size, checksumSha256, filename: file.name }),
    }));
    if (session.asset) return assetIdOf(session.asset);
    if (!session.uploadSessionId || !session.uploadUrl) {
      throw new DocumentApiError("The file service did not create an upload session.", "UPLOAD_SESSION_INVALID", 502, true);
    }
    const upload = await this.fetcher(session.uploadUrl, {
      method: "PUT",
      headers: session.requiredHeaders,
      body: file,
      credentials: "omit",
    });
    if (!upload.ok) throw new DocumentApiError("The file upload failed.", "UPLOAD_FAILED", upload.status, upload.status >= 500);
    const finalized = finalizeUploadResponseSchema.parse(await this.apiRequest(`/v1/document/upload-sessions/${encodeURIComponent(session.uploadSessionId)}/finalize`, {
      method: "POST",
      body: JSON.stringify({ filename: file.name }),
    }));
    return assetIdOf(finalized.asset);
  }

  async createJob(request: CreateJobRequest): Promise<DocumentJob> {
    return documentJobResponseSchema.parse(await this.apiRequest("/v1/document/jobs", {
      method: "POST",
      body: JSON.stringify(request),
    })).job;
  }

  async getJob(jobId: string): Promise<DocumentJob> {
    return documentJobResponseSchema.parse(await this.apiRequest(`/v1/document/jobs/${encodeURIComponent(jobId)}`)).job;
  }

  async listJobs(): Promise<readonly DocumentJob[]> {
    return documentJobsResponseSchema.parse(await this.apiRequest("/v1/document/jobs")).jobs;
  }

  async cancelJob(jobId: string): Promise<DocumentJob> {
    return documentJobResponseSchema.parse(await this.apiRequest(`/v1/document/jobs/${encodeURIComponent(jobId)}/cancel`, { method: "POST" })).job;
  }

  async retryJob(jobId: string): Promise<DocumentJob> {
    return documentJobResponseSchema.parse(await this.apiRequest(`/v1/document/jobs/${encodeURIComponent(jobId)}/retry`, { method: "POST" })).job;
  }

  async createDownloadUrl(assetId: string): Promise<string> {
    return downloadUrlResponseSchema.parse(await this.apiRequest(`/v1/document/files/${encodeURIComponent(assetId)}/download-url`, { method: "POST" })).downloadUrl;
  }

  async listFiles(): Promise<readonly DocumentAsset[]> {
    return documentFilesResponseSchema.parse(await this.apiRequest("/v1/document/files?status=active&limit=100")).assets;
  }

  async getUsage(): Promise<DocumentUsage> {
    return documentUsageResponseSchema.parse(await this.apiRequest("/v1/document/usage")).usage;
  }
}

export const documentApiClient = new DocumentApiClient();
